/**
 * 关键词水平条形图组件
 */
import React, { useMemo } from 'react';
import EChartCommon from '@op/components/EChartCommon';
import { DBO_V, DBO_EC } from '@op/utils/format';
import { getFilterDateQOQText } from '@op/utils/time';
import useLanguage from '@op/hooks/useLanguage';
import { getPercentageInfo } from '@op/utils';
import { formatChartParamsDate } from '@op/components/ChartCommon/formatter';
import { IKeywordItemWithQoq } from '@op/pages/SecurityWeeklyReport/hooks/useKeywordData';
import { useChartFootBtn } from '@op/hooks/useChartFootBtn';
import { ClassSpecial } from '@op/constants/common';

// 设计稿指定的蓝色
const BAR_COLOR = '#5086F3';

interface IKeywordBarChartProps {
  data: IKeywordItemWithQoq[];
  getKeywordName: (item: any) => string;
  height: number;
  filter?: any;
  onFeedsClick: (keyword: string | null) => void;
}

const KeywordBarChart: React.FC<IKeywordBarChartProps> = ({
  data,
  getKeywordName,
  height,
  filter,
  onFeedsClick,
}) => {
  const { formatMessage } = useLanguage();
  const { text: qoqText } = getFilterDateQOQText(filter);

  // 使用 useChartFootBtn 生成底部按钮
  const { getBottomClickBtn, divElementRef } = useChartFootBtn({
    filter,
    buttons: [
      {
        key: 'feeds',
        labelId: 'Click to go to [Feeds]',
      },
    ],
    onClick: ({ dataAttrs }) => onFeedsClick(dataAttrs.keyword),
  });

  const option = useMemo(() => {
    if (!data || data.length === 0) return {};

    // 按声量降序排序，声量大的在前面
    const displayData = [...data].sort((a, b) => b.mentions - a.mentions);
    // 反转数组，让声量大的显示在图表最上方（ECharts category轴第一个元素在下方）
    const keywords = displayData.map(item => item.keyword).reverse();
    const values = displayData.map((item) => item.mentions).reverse();
    const maxValue = Math.max(...values);

    // 计算total用于占比
    const total = displayData.reduce(
      (sum, item) => sum + (item.mentions || 0),
      0,
    );

    return {
      legend: {
        data: [
          {
            name: formatMessage({ id: 'Mentions' }),
            itemStyle: { color: BAR_COLOR },
          },
        ],
        bottom: 0,
      },
      // 支持竖向滚动
      dataZoom: [
        {
          type: 'inside', // 内置型 dataZoom，支持鼠标滚轮缩放
          yAxisIndex: [0, 1],
          filterMode: 'none',
          maxValueSpan: 9,
          minValueSpan: 9,
          startValue: 100,
          endValue: 100,
        },
        {
          type: 'slider',
          filterMode: 'none',
          show: true,
          yAxisIndex: [0, 1],
          width: 0,
          maxValueSpan: 9,
          minValueSpan: 9,
          startValue: 0,
          endValue: 100,
          right: 10,
          showDetail: false,
          showDataShadow: false,
          moveHandleSize: 8,
          moveHandleIcon: 'none',
          zoomLock: true,
          borderColor: 'rgba(0,0,0,0)',
          moveHandleStyle: { color: '#D8DAE4', borderCap: 'square' },
          emphasis: { moveHandleStyle: { color: '#D8DAE4' } },
        },
      ],
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        className: ClassSpecial.CHART_TOOLTIP_SCROLL,
        enterable: true,
        confine: false,
        position: DBO_EC.enterablePosition,
        formatter: (params: any) => { 
          return formatChartParamsDate({
            params,
            total,
            valueFormat: (value: number) => DBO_V.reviews(value, 2),
            dealwithRecords(records, total) {
              const newRecords: Array<any> = records.map((item) => {
                // 通过 value 匹配原始数据（因为数据被 reverse 过）
                const dataItem = displayData.find(
                  (d) => d.mentions === item.originValue,
                );
                const qoqRate = dataItem?.qoq_rate ?? 0;
                const share = getPercentageInfo(
                  Math.abs(item.originValue || 0),
                  total,
                  2,
                );
                return {
                  ...item,
                  share,
                  qoq: DBO_V.ratio(qoqRate, '-', 0),
                };
              });
              return newRecords.filter((item) => Boolean(item));
            },
            tablesConfigFunc: (records: any) => {
              const columns = [
                {
                  key: 'text',
                  header: params[0]?.name || '',
                },
                {
                  key: 'value',
                  align: 'right',
                  weight: 'bold',
                  header: formatMessage({ id: 'Mentions' }),
                },
                {
                  key: 'share',
                  align: 'right',
                  weight: 'bold',
                  header: formatMessage({ id: 'Share-1' }),
                },
                {
                  key: 'qoq',
                  align: 'right',
                  weight: 'bold',
                  header: formatMessage({ id: qoqText }),
                },
              ];
              return [
                {
                  showHeader: true,
                  records,
                  columns,
                },
              ];
            },
            footerText: getBottomClickBtn({
              keyword: params[0]?.name || '',
            }),
          });
        },
      },
      xAxis: {
        type: 'value',
        max: maxValue * 1.5,
        boundaryGap: ['0%', '0%'], // 数值轴必须使用百分比字符串数组
        splitLine: {
          show: true,
          lineStyle: { color: '#e8e8e8', type: 'solid' },
        },
        axisLine: { show: true, lineStyle: { color: '#E8E8E8' } },
        axisTick: { show: false },
        axisLabel: { show: true, formatter: DBO_EC.reviews },
      },
      // 双Y轴配置
      yAxis: [
        // 左侧Y轴：类别轴（关键词名称）
        {
          type: 'category',
          position: 'left',
          data: keywords,
          axisLine: { show: false },
          axisTick: { show: false },
          splitLine: { show: false },
          axisLabel: {
            interval: 0,
            show: true,
            formatter: (value: string) => `{keyword|${getKeywordName(value)}}`,
            rich: {
              keyword: {
                align: 'left',
              },
            },
            padding: [0, 0, 0, 0],
            overflow: 'break',
            fontWeight: 400,
            fontSize: 12,
            lineHeight: 16,
            width: 100,
          },
        },
        // 右侧Y轴：显示声量(qoq_rate)
        {
          type: 'category',
          position: 'right',
          data: displayData
            .map((item) => {
              const mentions = DBO_V.reviews(item.mentions ?? 0);
              const qoq = DBO_V.ratio(item.qoq_rate ?? 0, '-', 0);
              return `${mentions}(${qoq})`;
            })
            .reverse(),
          axisLine: { show: false },
          axisTick: { show: false },
          splitLine: { show: false },
          axisLabel: {
            color: '#999',
            fontSize: 12,
            width: 100,
          },
        },
      ],
      series: [
        {
          name: formatMessage({ id: 'Mentions' }),
          type: 'bar',
          data: values,
          barWidth: 16,
          itemStyle: {
            color: BAR_COLOR,
            borderRadius: [0, 2, 2, 0],
          },
        },
      ],
    };
  }, [data, getKeywordName, filter, getBottomClickBtn]);

  return (
    <div ref={divElementRef} style={{ position: 'relative' }}>
      <EChartCommon
        leftTitle={formatMessage({ id: 'Keyword' })}
        rightTitle={`${formatMessage({ id: 'Mentions' })} (${formatMessage({
          id: qoqText,
        })})`}
        options={option}
        height={height}
      />
    </div>
  );
};

export default KeywordBarChart;
